import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Star, Mail, Phone, Briefcase, Award, 
  MessageSquare, Calendar, User, MapPin, GraduationCap,
  TrendingUp, CheckCircle, Clock, Loader2, Eye, X, Send
} from 'lucide-react';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import AskQuestionModal from '@components/mentor/AskQuestionModal';
import FeatureRestrictionModal from '@components/common/FeatureRestrictionModal';
import mentorService from '@services/mentor.service';
import mentorFeedbackService from '@services/mentorFeedback.service';
import profileExtendedService from '@services/profileExtended.service';
import apiService from '@services/api/api.service';
import toast from 'react-hot-toast';
import { ROUTES } from '@constants';
import { usePlanRestrictions } from '@hooks/usePlanRestrictions';
import { useAuth } from '@hooks/useAuth';

const MentorDetails = () => {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const { canAccessFeature, getRestrictionMessage } = usePlanRestrictions();
  const [mentor, setMentor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);
  const [skills, setSkills] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [stats, setStats] = useState({
    questionsAnswered: 0,
    averageResponseTime: 'N/A',
    successRate: 0,
    totalStudents: 0
  });
  const [viewingExpertise, setViewingExpertise] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [canSubmitFeedback, setCanSubmitFeedback] = useState(false);
  const [feedbackEligibilityReason, setFeedbackEligibilityReason] = useState('');
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    fetchMentorDetails();
    fetchFeedbacks();
    checkFeedbackEligibility();
  }, [mentorId]);

  const fetchMentorDetails = async () => {
    try {
      setLoading(true);
      const response = await mentorService.getMentorById(mentorId);
      
      if (response.success) {
        const mentorData = response.data?.data || response.data;
        setMentor(mentorData);
      }

      // Fetch mentor stats (questions answered, students helped)
      try {
        const statsResponse = await apiService.get(`/profile/mentor/${mentorId}/stats`);
        if (statsResponse.success) {
          const statsData = statsResponse.data?.data || statsResponse.data;
          setStats({
            questionsAnswered: statsData.questionsAnswered || 0,
            averageResponseTime: statsData.averageResponseTime || 'N/A',
            successRate: statsData.successRate || 0,
            totalStudents: statsData.studentsHelped || 0
          });
        }
      } catch (err) {
        console.error('Failed to fetch mentor stats:', err);
        setStats({
          questionsAnswered: 0,
          averageResponseTime: 'N/A',
          successRate: 0,
          totalStudents: 0
        });
      }

      // Fetch full profile data
      try {
        const profileResponse = await apiService.get(`/profile/${mentorId}`);
        if (profileResponse.success) {
          const fullProfile = profileResponse?.data?.data || profileResponse?.data;
          setProfileData(fullProfile);
        }
      } catch (err) {
        console.error('Failed to fetch full profile:', err);
      }

      // Fetch skills
      try {
        const skillsResponse = await profileExtendedService.getSkillsByUserId(mentorId);
        if (skillsResponse.success) {
          const skillsData = skillsResponse.data?.data || skillsResponse.data || [];
          setSkills(Array.isArray(skillsData) ? skillsData : []);
        }
      } catch (err) {
        console.error('Failed to fetch skills:', err);
      }
    } catch (error) {
      console.error('Failed to fetch mentor details:', error);
      toast.error('Failed to load mentor details');
      
      setMentor({
        id: mentorId,
        firstName: 'Mr. Ajay',
        lastName: 'Patil',
        expertise: 'Expert',
        rating: 'N/A',
        totalReviews: 0,
        email: 'ajay.patil@example.com',
        phone: '+91 98765 43210',
        experience: '10+ Years',
        bio: 'Experienced professional with expertise in mentoring and guiding students.',
        location: 'Mumbai, India',
        qualification: 'MBA, B.Tech',
        joinedDate: new Date().toISOString(),
        specializations: ['Career Guidance', 'Interview Preparation', 'Skill Development']
      });
      
      setStats({
        questionsAnswered: 0,
        averageResponseTime: 'N/A',
        successRate: 0,
        totalStudents: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAskQuestion = () => {
    if (!canAccessFeature('connect_mentor')) {
      setShowRestrictionModal(true);
      return;
    }
    
    setShowQuestionModal(true);
  };

  const handleSubmitQuestion = async (data) => {
    try {
      const response = await mentorService.askQuestion(data);
      toast.success('Your question has been sent to the mentor successfully!');
      return response;
    } catch (error) {
      console.error('Failed to submit question:', error);
      toast.error(error.response?.data?.message || 'Failed to send question. Please try again.');
      throw error;
    }
  };

  const fetchFeedbacks = async () => {
    try {
      const response = await mentorFeedbackService.getMentorFeedbacks(mentorId, 10, 0);
      if (response.success) {
        setFeedbacks(response.data?.data?.feedbacks || []);
      }
    } catch (error) {
      console.error('Failed to fetch feedbacks:', error);
    }
  };

  const checkFeedbackEligibility = async () => {
    if (!authUser || authUser.role !== 'student') {
      setCanSubmitFeedback(false);
      return;
    }
    
    try {
      const response = await mentorFeedbackService.canSubmitFeedback(mentorId);
      console.log('Feedback eligibility response:', response);
      if (response.success) {
        setCanSubmitFeedback(response.data?.data?.canSubmit || false);
        setFeedbackEligibilityReason(response.data?.data?.reason || '');
      } else {
        setCanSubmitFeedback(false);
        setFeedbackEligibilityReason('Unable to check feedback eligibility');
      }
    } catch (error) {
      console.error('Failed to check feedback eligibility:', error);
      setCanSubmitFeedback(false);
      setFeedbackEligibilityReason('You need to ask at least one question to this mentor and receive an answer before submitting feedback.');
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackRating || !feedbackComment.trim()) {
      toast.error('Please provide both rating and comment');
      return;
    }

    if (feedbackComment.trim().length < 10) {
      toast.error('Comment must be at least 10 characters long');
      return;
    }

    setSubmittingFeedback(true);
    try {
      const response = await mentorFeedbackService.submitFeedback(
        mentorId,
        feedbackRating,
        feedbackComment.trim()
      );
      
      if (response.success) {
        toast.success('Feedback submitted successfully!');
        setShowFeedbackForm(false);
        setFeedbackRating(0);
        setFeedbackComment('');
        fetchFeedbacks();
        checkFeedbackEligibility();
        fetchMentorDetails();
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error(error.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 text-secondary-500 animate-spin" />
      </div>
    );
  }

  if (!mentor) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <User className="w-16 h-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-bold text-primary-700 mb-2">Mentor Not Found</h3>
        <Button onClick={() => navigate(ROUTES.STUDENT_MENTOR)} variant="outline">
          Back to Mentors
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Back Button */}
      <Button 
        variant="outline" 
        onClick={() => {
          if (authUser?.role === 'mentor') {
            const from = location.state?.from;
            if (from === 'profile') {
              navigate(ROUTES.PROFILE);
            } else {
              navigate(ROUTES.MENTOR_MENTORS_LIST);
            }
          } else {
            navigate(ROUTES.STUDENT_MENTOR);
          }
        }}
        className="mb-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {authUser?.role === 'mentor' 
          ? (location.state?.from === 'profile' ? 'Back to Profile' : 'Back to Mentors')
          : 'Back to Mentors'
        }
      </Button>

      {/* Header Section */}
      <Card className="p-4 md:p-5">
        <div className="flex flex-col md:flex-row gap-4 items-start">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-3xl md:text-4xl font-bold text-white">
                {mentor.firstName?.charAt(0) || 'M'}
              </span>
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-bold text-primary-700 mb-1">
                  {mentor.firstName && mentor.lastName 
                    ? `${mentor.firstName} ${mentor.lastName}` 
                    : mentor.name || 'Mentor'}
                </h1>
                
                {/* Current Position */}
                {mentor.currentPosition && (
                  <p className="text-sm md:text-base font-semibold text-secondary-600 mb-1">
                    {mentor.currentPosition}
                  </p>
                )}
                
                {/* Years of Experience */}
                {(mentor.yearsOfExperience || profileData?.yearsOfExperience) && (
                  <div className="flex items-center gap-2 text-xs md:text-sm text-primary-600 mb-2">
                    <TrendingUp className="w-3.5 h-3.5" />
                    <span className="font-medium">
                      {mentor.yearsOfExperience || profileData.yearsOfExperience} years of experience
                    </span>
                  </div>
                )}

                {/* Rating */}
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="flex items-center gap-1 bg-yellow-50 px-2.5 py-1 rounded-full border border-yellow-200">
                    <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                    <span className="text-sm font-bold text-primary-700">
                      {mentor.averageRating || 'N/A'}
                    </span>
                    {mentor.totalRatings > 0 && (
                      <span className="text-xs text-gray-600">
                        ({mentor.totalRatings})
                      </span>
                    )}
                  </div>
                </div>

                {/* Bio */}
                {(mentor.bio || mentor.careerGoal || profileData?.bio || profileData?.careerGoal) && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs md:text-sm text-gray-700 leading-relaxed line-clamp-2">
                      {mentor.bio || mentor.careerGoal || profileData.bio || profileData.careerGoal}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Button - Right Corner */}
              {authUser?.role !== 'mentor' && (
                <div className="flex-shrink-0">
                  <Button 
                    variant="primary" 
                    onClick={handleAskQuestion}
                    className="w-full sm:w-auto whitespace-nowrap"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Ask Question
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Section */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 text-center">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <CheckCircle className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-xl md:text-2xl font-bold text-primary-700 mb-0.5">
            {stats.questionsAnswered}
          </p>
          <p className="text-xs text-gray-600">Questions Answered</p>
        </Card>

        <Card className="p-3 text-center">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-xl md:text-2xl font-bold text-primary-700 mb-0.5">
            {stats.successRate}%
          </p>
          <p className="text-xs text-gray-600">Success Rate</p>
        </Card>

        <Card className="p-3 text-center">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <Clock className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-xl md:text-2xl font-bold text-primary-700 mb-0.5">
            {stats.averageResponseTime}
          </p>
          <p className="text-xs text-gray-600">Avg Response Time</p>
        </Card>

        <Card className="p-3 text-center">
          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <User className="w-5 h-5 text-orange-600" />
          </div>
          <p className="text-xl md:text-2xl font-bold text-primary-700 mb-0.5">
            {stats.totalStudents}
          </p>
          <p className="text-xs text-gray-600">Students Helped</p>
        </Card>
      </div>

      {/* Main Content */}
      <div className="space-y-4">
          {/* About */}
          {mentor.bio && (
            <Card>
              <h3 className="text-base md:text-lg font-bold text-primary-700 mb-3">About</h3>
              <p className="text-xs md:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {mentor.bio}
              </p>
            </Card>
          )}

          {/* Professional Experience & Skills Grid */}
          {(profileData?.currentCompany || profileData?.currentPosition || profileData?.yearsOfExperience || (skills && skills.length > 0)) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Professional Experience */}
              {(profileData?.currentCompany || profileData?.currentPosition || profileData?.yearsOfExperience) && (
                <Card>
                  <h3 className="text-base md:text-lg font-bold text-primary-700 mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Professional Experience
                  </h3>
                  <div className="space-y-3">
                    {profileData.currentCompany && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Current Company
                        </label>
                        <p className="text-sm text-gray-900">{profileData.currentCompany}</p>
                      </div>
                    )}
                    {profileData.currentPosition && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Current Position
                        </label>
                        <p className="text-sm text-gray-900">{profileData.currentPosition}</p>
                      </div>
                    )}
                    {profileData.yearsOfExperience && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Years of Experience
                        </label>
                        <p className="text-sm text-gray-900">{profileData.yearsOfExperience} years</p>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Skills */}
              {skills && skills.length > 0 && (
                <Card>
                  <h3 className="text-base md:text-lg font-bold text-primary-700 mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skills.map((skill) => (
                      <div
                        key={skill.id}
                        className="px-3 py-2 bg-gradient-to-r from-secondary-50 to-primary-50 border border-secondary-200 rounded-lg"
                      >
                        <span className="text-sm font-medium text-gray-900">{skill.skillName}</span>
                        {skill.proficiency && (
                          <span className="ml-2 text-xs text-secondary-600">
                            ({skill.proficiency})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Areas of Expertise */}
          {(mentor.expertise && mentor.expertise.length > 0) || (mentor.specializations && mentor.specializations.length > 0) ? (
            <Card>
              <h3 className="text-base md:text-lg font-bold text-primary-700 mb-3 flex items-center gap-2">
                <Award className="w-4 h-4" />
                Areas of Expertise
              </h3>
              
              {/* Display mentor expertise from database */}
              {mentor.expertise && Array.isArray(mentor.expertise) && mentor.expertise.length > 0 && (
                <div className="overflow-x-auto pb-2">
                  <div className="flex gap-3 min-w-max">
                    {mentor.expertise.map((exp) => (
                      <div 
                        key={exp.id || exp.industryName} 
                        className="flex-shrink-0 w-72 p-3 bg-gradient-to-r from-secondary-50 to-primary-50 rounded-lg border border-secondary-200 relative"
                      >
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="px-3 py-1 bg-secondary-600 text-white rounded-full text-xs font-semibold">
                              {exp.industryName}
                            </span>
                            {exp.isCustomJob && (
                              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                                Custom
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => setViewingExpertise(exp)}
                            className="flex-shrink-0 p-1.5 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                        <h4 className="text-sm font-bold text-primary-700 mb-1.5">
                          {exp.jobTitle}
                        </h4>
                        <p className="text-xs text-gray-700 leading-relaxed line-clamp-3">
                          {exp.expertiseDescription}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Display specializations if available */}
              {mentor.specializations && mentor.specializations.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {mentor.specializations.map((spec, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-secondary-50 text-secondary-700 rounded-lg text-sm font-medium border border-secondary-200"
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              )}
            </Card>
          ) : null}

          {/* Awards & Honours */}
          {profileData?.awards && profileData.awards.length > 0 && (
            <Card>
              <h3 className="text-base md:text-lg font-bold text-primary-700 mb-3 flex items-center gap-2">
                <Award className="w-4 h-4" />
                Awards & Honours
              </h3>
              <div className="space-y-3">
                {profileData.awards.map((award, index) => (
                  <div key={index} className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                    <div className="inline-block px-3 py-1 bg-primary-600 text-white rounded-full text-xs font-medium mb-2">
                      {award.type === 'accolades' ? 'Accolades (Office)' : 'Achievements (Sports, Exhibitions, Debates)'}
                    </div>
                    {award.note && (
                      <p className="text-sm text-gray-700 leading-relaxed mt-2">{award.note}</p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Feedback Section */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base md:text-lg font-bold text-primary-700">Student Feedback</h3>
              {authUser?.role === 'student' && !showFeedbackForm && (
                <div className="relative group">
                  <Button
                    variant={canSubmitFeedback ? "primary" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (canSubmitFeedback) {
                        setShowFeedbackForm(true);
                      } else {
                        toast.error(feedbackEligibilityReason || 'You cannot submit feedback at this time');
                      }
                    }}
                    disabled={!canSubmitFeedback}
                    title={!canSubmitFeedback ? feedbackEligibilityReason : 'Share your experience with this mentor'}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Add Feedback
                  </Button>
                  {!canSubmitFeedback && feedbackEligibilityReason && (
                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-10 w-64">
                      <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                        <div className="flex items-start gap-2">
                          <MessageSquare className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <p className="leading-relaxed">{feedbackEligibilityReason}</p>
                        </div>
                        <div className="absolute top-full right-4 -mt-1">
                          <div className="border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Feedback Form */}
            {showFeedbackForm && (
              <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-primary-700">Submit Your Feedback</h4>
                  <button
                    onClick={() => {
                      setShowFeedbackForm(false);
                      setFeedbackRating(0);
                      setFeedbackComment('');
                    }}
                    className="p-1 hover:bg-white rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>

                {/* Rating */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rating <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFeedbackRating(star)}
                        className="transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-8 h-8 ${
                            star <= feedbackRating
                              ? 'text-yellow-500 fill-yellow-500'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                    {feedbackRating > 0 && (
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        {feedbackRating} / 5
                      </span>
                    )}
                  </div>
                </div>

                {/* Comment */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Feedback <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={feedbackComment}
                    onChange={(e) => setFeedbackComment(e.target.value)}
                    placeholder="Share your experience with this mentor..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent resize-none text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum 10 characters ({feedbackComment.length}/10)
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowFeedbackForm(false);
                      setFeedbackRating(0);
                      setFeedbackComment('');
                    }}
                    disabled={submittingFeedback}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmitFeedback}
                    disabled={submittingFeedback || !feedbackRating || feedbackComment.trim().length < 10}
                  >
                    {submittingFeedback ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Feedback
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Display Feedbacks */}
            <div className="space-y-3">
              {feedbacks.length > 0 ? (
                feedbacks.map((feedback) => (
                  <div key={feedback.id} className="border-l-4 border-secondary-500 pl-4 py-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= feedback.rating
                                ? 'text-yellow-500 fill-yellow-500'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {feedback.student?.firstName && feedback.student?.lastName
                          ? `${feedback.student.firstName} ${feedback.student.lastName}`
                          : 'Anonymous Student'}
                      </span>
                      <span className="text-xs text-gray-500">
                        • {new Date(feedback.feedbackDate).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      "{feedback.comment}"
                    </p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No feedback yet</p>
                  {authUser?.role === 'student' && canSubmitFeedback && (
                    <p className="text-xs text-gray-400 mt-1">Be the first to share your experience!</p>
                  )}
                </div>
              )}
            </div>
          </Card>
      </div>

      {/* Expertise View Modal */}
      {viewingExpertise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                  <Eye className="w-4 h-4 text-primary-700" />
                </div>
                <h3 className="text-lg font-bold text-primary-800">Expertise Details</h3>
              </div>
              <button
                onClick={() => setViewingExpertise(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="px-3 py-1.5 bg-secondary-600 text-white rounded-lg text-sm font-semibold">
                  {viewingExpertise.industryName}
                </span>
                {viewingExpertise.isCustomJob && (
                  <span className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-sm font-medium">
                    Custom Job
                  </span>
                )}
              </div>

              <div className="mb-6">
                <h4 className="text-xl font-bold text-primary-800 mb-2">
                  {viewingExpertise.jobTitle}
                </h4>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-200 rounded-lg p-5">
                <h5 className="text-sm font-semibold text-primary-800 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  What I'm Offering / My Expertise
                </h5>
                <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {viewingExpertise.expertiseDescription}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  type="button"
                  onClick={() => setViewingExpertise(null)}
                  variant="primary"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ask Question Modal */}
      <AskQuestionModal
        mentor={mentor}
        isOpen={showQuestionModal}
        onClose={() => setShowQuestionModal(false)}
        onSubmit={handleSubmitQuestion}
        onHistoryClick={() => {
          setShowQuestionModal(false);
          navigate(ROUTES.STUDENT_MY_QUESTIONS);
        }}
      />

      <FeatureRestrictionModal
        isOpen={showRestrictionModal}
        onClose={() => setShowRestrictionModal(false)}
        onUpgrade={() => {
          setShowRestrictionModal(false);
          navigate(ROUTES.STUDENT_PLAN_UPGRADE);
        }}
        featureName="Connect with Mentors"
      />
    </div>
  );
};

export default MentorDetails;
